/**
 * Pulso · lado del navegador (P1, 7-oct-2026). GENÉRICO: no importa nada de la app (se copia tal cual a otra).
 *
 * Le dice al backend QUÉ PANTALLA está abierta, sin pedidos nuevos: agrega la cabecera `X-Pulso-Pantalla` a las
 * llamadas que la app ya hace. Tres reglas:
 *
 * 1. **La pestaña en segundo plano se marca.** Una pestaña olvidada sigue pidiendo (sondeos por reloj), pero no es
 *    una persona mirando: la cabecera lleva `~` delante de la clave. El backend sabe qué pantalla es y NO la cuenta
 *    como uso de pantalla (P3; antes no se mandaba nada y no se podía distinguir de un navegador sin actualizar).
 * 2. **El valor es una clave corta en ASCII** (`seccion.modulo`, `_inicio`…), nunca la URL: una cabecera con un
 *    carácter raro hace que el navegador rechace el pedido antes de salir.
 * 3. **Nada de acá puede romper un pedido.** Todo va en try/catch.
 *
 * El latido (`GET /_pulso/latido`) sale solo si la pestaña está a la vista y pasaron 3 minutos sin ningún pedido: en
 * las pantallas que se refrescan solas no late nunca. Si el backend contesta 404 (Pulso apagado), deja de latir.
 */
export const CABECERA = "X-Pulso-Pantalla";
export const MARCA_FONDO = "~";
export const RUTA_LATIDO = "/_pulso/latido";
export const QUIETO_MS = 3 * 60_000;
export const REVISAR_MS = 60_000;

const FORMA = /^[a-z0-9_]{1,40}(\.[a-z0-9_]{1,40})?$/;

let pantalla = null;
let ultimoPedido = 0;
let apagado = false;
let reloj = null;

/**
 * PURA. La clave de la pantalla abierta. `resolver(secciones, pathname)` es la cuenta que la app ya tiene para
 * saber qué módulo del menú es una ruta (devuelve `{ seccion, modulo }`).
 */
export function clavePantalla(secciones, pathname, resolver) {
  const ruta = String(pathname || "/");
  if (ruta === "/" || ruta === "") return "_inicio";
  if (ruta === "/chofer" || ruta.startsWith("/chofer/")) return "_app_chofer";
  if (ruta === "/login" || ruta.startsWith("/activar")) return null;
  let hallado = null;
  try {
    hallado = resolver ? resolver(secciones, ruta) : null;
  } catch (_) {
    hallado = null;
  }
  const s = hallado?.seccion?.clave;
  const m = hallado?.modulo?.clave;
  return s && m ? `${s}.${m}` : "_otra";
}

/** La app avisa en qué pantalla está (cada vez que cambia la ruta). */
export function fijarPantalla(clave) {
  pantalla = typeof clave === "string" && FORMA.test(clave) ? clave : null;
}

/**
 * PURA (salvo el documento). Lo que va en la cabecera, o null si no hay que mandarla. Con la pestaña en segundo
 * plano va la misma clave con `~` delante.
 */
export function pantallaParaEnviar(doc = typeof document !== "undefined" ? document : null) {
  if (!pantalla || !doc) return null;
  return doc.visibilityState === "visible" ? pantalla : MARCA_FONDO + pantalla;
}

/** PURA. ¿Toca mandar un latido ahora? */
export function tocaLatir({ ahora, ultimo, visible, apagado: off, hayToken, hayPantalla }) {
  if (off || !visible || !hayToken || !hayPantalla) return false;
  return ahora - ultimo >= QUIETO_MS;
}

function hayToken() {
  try {
    return !!window.localStorage.getItem("qb_token");
  } catch (_) {
    return false;
  }
}

function latir(api) {
  const visible = typeof document !== "undefined" && document.visibilityState === "visible";
  if (!tocaLatir({ ahora: Date.now(), ultimo: ultimoPedido, visible, apagado, hayToken: hayToken(), hayPantalla: !!pantalla })) return;
  ultimoPedido = Date.now();
  Promise.resolve(api.get(RUTA_LATIDO, { __silent: true, validateStatus: () => true }))
    .then((r) => { if (r && r.status === 404) apagado = true; })
    .catch(() => { /* sin red o bloqueado: no pasa nada */ });
}

/** Engancha Pulso al cliente HTTP de la app (axios). Se llama una sola vez. */
export function instalarPulso(api) {
  try {
    api.interceptors.request.use((config) => {
      try {
        const esLatido = String(config?.url || "").endsWith(RUTA_LATIDO);
        if (!esLatido) ultimoPedido = Date.now();
        const clave = pantallaParaEnviar();
        if (clave && config && config.headers) config.headers[CABECERA] = clave;
        if (!reloj && typeof setInterval === "function") {
          reloj = setInterval(() => { try { latir(api); } catch (_) { /* nunca rompe */ } }, REVISAR_MS);
        }
      } catch (_) {
        /* Pulso nunca rompe un pedido */
      }
      return config;
    });
  } catch (_) {
    /* un cliente sin interceptores: Pulso queda sin efecto */
  }
}

/** Solo para los tests: vuelve al estado inicial. */
export function _reiniciar() {
  if (reloj) clearInterval(reloj);
  pantalla = null;
  ultimoPedido = 0;
  apagado = false;
  reloj = null;
}
